import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import "./Home.css";

/* =========================================================
   HERO SLIDES DATA
   ========================================================= */

const SLIDE_DURATION = 6000;

const slideData = [
  {
    id: "01",
    img: "/images/slides/PromoSlide1.png",
    badge: "DIGITAL AGENCY",
    title: "Smart Digital Solutions for South African Businesses",
    desc: "We design and develop high-performance websites, web applications, online stores, and enterprise business software designed to turn traffic into measurable revenue.",
    tags: ["Custom Web", "React/Node", "E-Commerce"],
    route: "/services",
    action: "Explore Services",
  },
  {
    id: "02",
    img: "/images/slides/PromoSlide2.png",
    badge: "E-COMMERCE PLATFORMS",
    title: "Your Brand. Your Store. Uncapped Growth.",
    desc: "Launch your custom online store with local South African payment gateways (PayFast/Ozow), instant automated invoicing, and real-time inventory management.",
    tags: ["PayFast & Ozow", "Custom Domains", "High Converting"],
    route: "/ecommerce",
    action: "Explore Online Stores",
  },
  {
    id: "03",
    img: "/images/slides/PromoSlide3.png",
    badge: "BUSINESS AUTOMATION",
    title: "Build. Automate. Scale Effortlessly.",
    desc: "Streamline operations with tailored administrative portals, BI dashboards, automated workflow pipelines, and ongoing enterprise-grade support.",
    tags: ["Custom Dashboards", "API Integration", "24/7 Monitoring"],
    route: "/services",
    action: "Explore Web Apps",
  },
];

/* =========================================================
   TRUST STRIP DATA
   ========================================================= */

const trustData = [
  {
    icon: "fa-search",
    label: "SEO-Optimized Builds",
    detail: "Rank higher on Google",
  },
  {
    icon: "fa-credit-card",
    label: "Local Payment Gateways",
    detail: "PayFast, Ozow, Yoco",
  },
  {
    icon: "fa-shield-alt",
    label: "Enterprise SSL & Security",
    detail: "POPIA compliant hosting",
  },
  {
    icon: "fa-headset",
    label: "Dedicated SLA Support",
    detail: "Maintenance & updates",
  },
];

/* =========================================================
   SERVICES DATA
   (featured cards form a zig-zag so the grid never has gaps)
   ========================================================= */

const servicesData = [
  {
    featured: true,
    icon: "fa-laptop-code",
    title: "High-Performance Modern Websites",
    desc: "Blazing fast, responsive, and SEO-friendly web platforms crafted specifically to showcase your enterprise brand and drive qualified conversion rates.",
    tags: ["React & Node.js", "SEO Optimized", "Responsive Design"],
    route: "/services",
  },
  {
    featured: false,
    icon: "fa-store",
    title: "Custom E-Commerce Stores",
    desc: "Multi-tenant or standalone online shopping stores powered by seamlessly integrated local SA payment gateways.",
    tags: ["Payment Gateways", "Inventory Sync"],
    route: "/ecommerce",
  },
  {
    featured: false,
    icon: "fa-code-branch",
    title: "Custom Web Applications & Portals",
    desc: "Purpose-built web applications and administrative portals engineered around your team's exact internal workflows.",
    tags: ["Custom Workflows", "Role-Based Access", "API Connectors"],
    route: "/services",
  },
  {
    featured: true,
    icon: "fa-server",
    title: "Infrastructure & Managed Hosting",
    desc: "Ultra-reliable cloud deployment, automated daily backups, SSL certification, and zero-downtime server setups.",
    tags: ["99.9% Uptime", "Automated Backups"],
    route: "/services",
  },
  {
    featured: true,
    icon: "fa-chart-pie",
    title: "Business Analytics & Dashboards",
    desc: "Turn raw operational databases into real-time interactive BI dashboards and executive performance reports.",
    tags: ["BI Reporting", "Data Pipelines"],
    route: "/services",
  },
  {
    featured: false,
    icon: "fa-shield-virus",
    title: "Managed Maintenance & Technical SLA",
    desc: "Continuous technical maintenance, security monitoring, infrastructure patching, and priority on-call support.",
    tags: ["On-Call Support", "Patch Management"],
    route: "/services",
  },
];

/* =========================================================
   PROCESS DATA
   ========================================================= */

const processData = [
  {
    step: "01",
    title: "Discovery & Strategy",
    desc: "We dive deep into your operational processes, target demographic, and growth goals to build a clear project blueprint.",
  },
  {
    step: "02",
    title: "Architectural Design",
    desc: "We wireframe and craft an accessible, on-brand user experience tailored specifically for intuitive user engagement.",
  },
  {
    step: "03",
    title: "Agile Engineering",
    desc: "We write clean, modular code and integrate scalable database structures with comprehensive security and speed testing.",
  },
  {
    step: "04",
    title: "Deployment & Growth",
    desc: "We launch your platform live to production and provide continuous technical maintenance and strategic optimization.",
  },
];

/* =========================================================
   HOOK: SCROLL REVEAL
   ========================================================= */

const useScrollReveal = () => {
  useEffect(() => {
    const elements = document.querySelectorAll(".tf-home .tf-reveal");

    if (!("IntersectionObserver" in window)) {
      elements.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -50px 0px" },
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
};

const pad = (n) => String(n).padStart(2, "0");

/* =========================================================
   MAIN COMPONENT
   ========================================================= */

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);

  const total = slideData.length;

  const goTo = useCallback(
    (index) => setCurrentSlide((index + total) % total),
    [total],
  );

  useScrollReveal();

  /* Preload background images */
  useEffect(() => {
    slideData.forEach((slide) => {
      const img = new Image();
      img.src = slide.img;
    });
  }, []);

  /* Auto-rotate slides */
  useEffect(() => {
    if (paused) return;

    const reduceMotion =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) return;

    const timer = setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % total);
    }, SLIDE_DURATION);

    return () => clearTimeout(timer);
  }, [currentSlide, paused, total]);

  /* Touch swiping */
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      goTo(delta < 0 ? currentSlide + 1 : currentSlide - 1);
    }
    touchStartX.current = null;
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowRight") goTo(currentSlide + 1);
    if (e.key === "ArrowLeft") goTo(currentSlide - 1);
  };

  const slide = slideData[currentSlide];

  return (
    <div className="tf-home">
      {/* =====================================================
          HERO SECTION
      ===================================================== */}
      <section
        className="tf-hero"
        aria-roledescription="carousel"
        aria-label="Featured digital services"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onKeyDown={handleKeyDown}
      >
        <div className="tf-hero-slides" aria-hidden="true">
          {slideData.map((s, index) => (
            <div
              key={s.id}
              className={`tf-hero-background ${
                index === currentSlide ? "active" : ""
              }`}
              style={{ backgroundImage: `url("${s.img}")` }}
            />
          ))}
        </div>

        <div className="tf-hero-overlay" aria-hidden="true" />
        <div className="tf-hero-glow tf-hero-glow-a" aria-hidden="true" />
        <div className="tf-hero-glow tf-hero-glow-b" aria-hidden="true" />
        <div className="tf-hero-grid-pattern" aria-hidden="true" />

        <div className="tf-container tf-hero-container">
          <div className="tf-hero-layout">
            {/* Main slide copy */}
            <div className="tf-hero-copy" aria-live="polite">
              <div key={currentSlide} className="tf-hero-text-wrapper">
                <div className="tf-pill-badge">
                  <span className="tf-pill-dot" aria-hidden="true" />
                  <span className="tf-pill-text">{slide.badge}</span>
                </div>

                <h1 className="tf-hero-title">{slide.title}</h1>

                <p className="tf-hero-desc">{slide.desc}</p>

                <div className="tf-hero-tags">
                  {slide.tags.map((tag) => (
                    <span key={tag} className="tf-hero-tag">
                      <i className="fas fa-check" aria-hidden="true" />
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="tf-hero-actions">
                <Link to={slide.route} className="tf-btn tf-btn-primary">
                  <span>{slide.action}</span>
                  <i className="fas fa-arrow-right" aria-hidden="true" />
                </Link>

                <Link to="/contact" className="tf-btn tf-btn-secondary">
                  <i className="fas fa-calendar-alt" aria-hidden="true" />
                  <span>Schedule Consultation</span>
                </Link>
              </div>

              <div className="tf-hero-metrics">
                <div className="tf-metric-item">
                  <span className="tf-metric-val">50+</span>
                  <span className="tf-metric-lbl">Engineered Systems</span>
                </div>
                <div className="tf-metric-divider" />
                <div className="tf-metric-item">
                  <span className="tf-metric-val">99.8%</span>
                  <span className="tf-metric-lbl">Uptime SLA</span>
                </div>
                <div className="tf-metric-divider" />
                <div className="tf-metric-item">
                  <span className="tf-metric-val">24/7</span>
                  <span className="tf-metric-lbl">Technical Monitoring</span>
                </div>
              </div>
            </div>

            {/* Interactive hero navigation panel */}
            <div className="tf-hero-selector">
              <div className="tf-selector-header">
                <span className="tf-selector-title">Selected capabilities</span>
                <span className="tf-selector-counter">
                  {pad(currentSlide + 1)} / {pad(total)}
                </span>
              </div>

              <div className="tf-selector-list">
                {slideData.map((item, idx) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`tf-selector-card ${
                      idx === currentSlide ? "is-active" : ""
                    }`}
                    onClick={() => goTo(idx)}
                    aria-label={`Show slide ${item.id}: ${item.badge}`}
                    aria-current={idx === currentSlide ? "true" : undefined}
                  >
                    <div className="tf-selector-num">{item.id}</div>
                    <div className="tf-selector-body">
                      <h4>{item.badge}</h4>
                      <p>{item.title}</p>
                    </div>
                  </button>
                ))}
              </div>

              <div className="tf-slide-progress-bar" aria-hidden="true">
                <div
                  key={`${currentSlide}-${paused}`}
                  className={`tf-slide-progress-fill ${
                    paused ? "paused" : "running"
                  }`}
                  style={{ animationDuration: `${SLIDE_DURATION}ms` }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          TRUST STRIP
      ===================================================== */}
      <section
        className="tf-trust-section"
        aria-label="Why businesses rely on us"
      >
        <div className="tf-container">
          <div className="tf-trust-bar tf-reveal">
            {trustData.map((item) => (
              <div key={item.label} className="tf-trust-card">
                <div className="tf-trust-icon-box">
                  <i className={`fas ${item.icon}`} aria-hidden="true" />
                </div>
                <div className="tf-trust-text">
                  <span className="tf-trust-title">{item.label}</span>
                  <span className="tf-trust-sub">{item.detail}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =====================================================
          SERVICES SECTION (BENTO GRID)
      ===================================================== */}
      <section className="tf-section tf-services-section">
        <div className="tf-container">
          <div className="tf-section-header tf-reveal">
            <span className="tf-section-kicker">Core competencies</span>
            <h2>
              Engineering Digital Products Built for{" "}
              <span>Scale & Performance</span>
            </h2>
            <p>
              From conversion-focused websites to complex web applications and
              automated data pipelines, we engineer robust digital
              infrastructure for growing enterprises.
            </p>
          </div>

          <div className="tf-services-bento">
            {servicesData.map((service, index) => (
              <Link
                to={service.route}
                key={service.title}
                className={`tf-bento-card ${
                  service.featured ? "tf-bento-featured" : ""
                } tf-reveal`}
                style={{ transitionDelay: `${(index % 3) * 80}ms` }}
              >
                <div className="tf-bento-top">
                  <div className="tf-bento-icon">
                    <i className={`fas ${service.icon}`} aria-hidden="true" />
                  </div>
                  <span className="tf-bento-number">{pad(index + 1)}</span>
                </div>

                <div className="tf-bento-body">
                  <h3>{service.title}</h3>
                  <p>{service.desc}</p>
                </div>

                <div className="tf-bento-footer">
                  <div className="tf-bento-pills">
                    {service.tags.map((t) => (
                      <span key={t}>{t}</span>
                    ))}
                  </div>
                  <span className="tf-bento-action" aria-hidden="true">
                    <i className="fas fa-arrow-right" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* =====================================================
          PROCESS SECTION
      ===================================================== */}
      <section className="tf-section tf-process-section">
        <div className="tf-container">
          <div className="tf-section-header tf-reveal">
            <span className="tf-section-kicker">Methodology</span>
            <h2>
              A Transparent Process Driven by <span>Technical Precision</span>
            </h2>
            <p>
              Direct communication, structured milestones, and regular progress
              demos ensure complete transparency from kickoff to live
              deployment.
            </p>
          </div>

          <div className="tf-process-timeline">
            {processData.map((step, index) => (
              <div
                key={step.step}
                className="tf-process-card tf-reveal"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="tf-process-header">
                  <span className="tf-process-num">{step.step}</span>
                  <div className="tf-process-node" aria-hidden="true" />
                </div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =====================================================
          CTA SECTION
      ===================================================== */}
      <section className="tf-cta-section">
        <div className="tf-container">
          <div className="tf-cta-box tf-reveal">
            <div className="tf-cta-inner">
              <span className="tf-section-kicker tf-kicker-light">
                Start your project
              </span>
              <h2>
                Ready to Upgrade Your Enterprise <span>Digital Presence?</span>
              </h2>
              <p>
                Get in touch with our team for a comprehensive technical
                consultation and detailed project estimate tailored to your
                exact requirements.
              </p>

              <div className="tf-cta-btns">
                <Link to="/contact" className="tf-btn tf-btn-accent">
                  <span>Start a Project</span>
                  <i className="fas fa-arrow-right" aria-hidden="true" />
                </Link>

                <Link to="/services" className="tf-btn tf-btn-ghost">
                  <span>Explore Capabilities</span>
                </Link>
              </div>
            </div>

            <div className="tf-cta-graphic" aria-hidden="true">
              <div className="tf-graphic-circle" />
              <span className="tf-graphic-brand">TF</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
